import { type ImportCisContractsSummary } from '@ebalance/proto/bffuser/v1/cis_contract_model_pb'
import { useTranslation } from 'react-i18next'

import {
  CsvDiffSummary,
  type CsvDiffSummaryItem,
} from '~/components/common/CsvUpload/CsvDiffSummary'

/**
 * Step2 サマリカードのクリックフィルタ値。'all' = 処理対象合計 (初期選択)。
 * 'diff' = 新規 + 変更 + 削除 (common.md AC-DUC-012「差分のみ」)。
 * 'deleted' は spec が delete 列を採用する data type のため
 * (common.md AC-DUC-012 / AC-DUC-013)。
 */
export type SummaryCardFilter =
  | 'all'
  | 'diff'
  | 'unchanged'
  | 'new'
  | 'changed'
  | 'deleted'
  | 'error'

type Props = {
  summary: ImportCisContractsSummary
  activeFilter: SummaryCardFilter
  onFilterChange: (filter: SummaryCardFilter) => void
}

/**
 * Step2 差分プレビューのサマリカード。 並びは design
 * (`admin/uploads/supply-point-master.html` `#summary`) の「新規 / 変更 / 変更なし /
 * 削除 / エラー / 差分のみ / 処理対象合計」 で、 クリック式フィルタとして表示する。
 *
 * 件数はすべて summary (サーバー計算値) から表示する — frontend で行を数え直さ
 * ない。据え置き行は row_results に含まれない
 * (レスポンスサイズ対策) ため、そもそも数え直しは不可能で、summary が唯一の SoT。
 * 処理対象合計は new+changed+unchanged+deleted の表示合成であり、エラー行は処理対象外
 * なので含めない (common.md「処理対象合計」の定義)。 エラー・削除は 0 件で隠す。
 *
 * 見た目 (segmented 準拠の pill・件数の色) と選択状態の表現 (`role="button"` +
 * `aria-pressed`、 キーボード操作) は CSV アップロード共通の `CsvDiffSummary` が持つ。
 */
export const SummaryCards = ({
  summary,
  activeFilter,
  onFilterChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const total =
    summary.newRowCount +
    summary.changedRowCount +
    summary.unchangedRowCount +
    summary.deletedRowCount

  const cards: Array<CsvDiffSummaryItem<SummaryCardFilter>> = [
    {
      filter: 'new',
      label: t('page:cisContractCsvUpload.step2.summary.new'),
      count: summary.newRowCount,
      tone: 'positive',
    },
    {
      filter: 'changed',
      label: t('page:cisContractCsvUpload.step2.summary.changed'),
      count: summary.changedRowCount,
      tone: 'warning',
    },
    {
      filter: 'unchanged',
      label: t('page:cisContractCsvUpload.step2.summary.unchanged'),
      count: summary.unchangedRowCount,
    },
    ...(summary.deletedRowCount > 0
      ? [
          {
            filter: 'deleted' as const,
            label: t('page:cisContractCsvUpload.step2.summary.deleted'),
            count: summary.deletedRowCount,
            tone: 'negative' as const,
          },
        ]
      : []),
    ...(summary.errorRowCount > 0
      ? [
          {
            filter: 'error' as const,
            label: t('page:cisContractCsvUpload.step2.summary.error'),
            count: summary.errorRowCount,
            tone: 'negative' as const,
          },
        ]
      : []),
    {
      filter: 'diff',
      label: t('page:cisContractCsvUpload.step2.summary.diff'),
      count:
        summary.newRowCount + summary.changedRowCount + summary.deletedRowCount,
    },
    {
      filter: 'all',
      label: t('page:cisContractCsvUpload.step2.summary.total'),
      count: total,
    },
  ]

  return (
    <CsvDiffSummary
      activeFilter={activeFilter}
      items={cards}
      onFilterChange={onFilterChange}
    />
  )
}
